import ShelfController from '../../core/shelf.controller';
import { User } from '../../data';
import { PERMISSIONS_KEY, canAccess } from '../../core/access';
import { isScreenEnabled } from '../../company/session';

const PRICE_SCREENS = ['price', 'distributor-prices', 'levantamiento-general'];

const toDraft = (roles) =>
    Object.fromEntries(
        (roles || []).map((r) => [r.role, { screens: new Set(r.screens || []), actions: new Set(r.actions || []) }])
    );

class PermissionsController extends ShelfController {
    async initialize() {
        if (!this.isAuthenticated) return this.navigateLogin();
        // Admin-only, unless the company lets an action open the console (core/access.js).
        if (!canAccess(User.getInstance()?.userType, PERMISSIONS_KEY)) {
            this.setState({ loading: false, denied: true });
            return;
        }
        await this.load();
    }

    async load() {
        this.setState({ loading: true });
        try {
            const res = await this.data.fetchPermissions();
            if (!res?.success) throw new Error(res?.message || 'No se pudieron cargar los permisos');
            const { actions, roles } = res.data;
            // Only screens the signed-in company has are offered. Keys the API stores for other
            // screens stay in each role's draft untouched.
            const screens = (res.data.screens || []).filter((screen) => isScreenEnabled(screen.key));
            this.setState({ screens, actions, roles, draft: toDraft(roles), dirty: new Set(), loading: false, error: null });
        } catch (err) {
            this.setState({ loading: false, error: err?.response?.data?.message || err?.message || 'No se pudieron cargar los permisos' });
        }
    }

    /** Matrix rows: one group header per screen group, its screens, then the "Funciones" group and its actions. */
    get rows() {
        const { screens, actions } = this.state;
        const rows = [];
        const groups = [...new Set(screens.map((s) => s.group))];
        for (const group of groups) {
            const inGroup = screens.filter((s) => s.group === group);
            rows.push({ key: `g:${group}`, type: 'group', label: `Pantallas · ${group}`, keys: inGroup.map((s) => s.key) });
            for (const s of inGroup) rows.push({ key: `s:${s.key}`, type: 'screens', id: s.key, label: s.label });
        }
        rows.push({ key: 'g:actions', type: 'group', label: 'Funciones', keys: actions.map((a) => a.key), kind: 'actions' });
        for (const a of actions) rows.push({ key: `a:${a.key}`, type: 'actions', id: a.key, label: a.label, description: a.description });
        return rows;
    }

    toggle(role, kind, key, checked) {
        this.setState((prev) => {
            const current = prev.draft[role];
            const nextSet = new Set(current[kind]);
            if (checked) nextSet.add(key);
            else nextSet.delete(key);
            const draft = { ...prev.draft, [role]: { ...current, [kind]: nextSet } };
            const dirty = new Set(prev.dirty);
            const saved = prev.roles.find((r) => r.role === role);
            const same = (a, b) => a.size === b.length && b.every((k) => a.has(k));
            if (same(draft[role].screens, saved.screens || []) && same(draft[role].actions, saved.actions || [])) dirty.delete(role);
            else dirty.add(role);
            return { draft, dirty };
        });
    }

    // Toggle a whole column section (all screens or all actions) for one role.
    toggleAll(role, kind, keys, checked) {
        keys.forEach((k) => this.toggle(role, kind, k, checked));
    }

    discard() {
        this.setState((prev) => ({ draft: toDraft(prev.roles), dirty: new Set() }));
    }

    async save() {
        const { dirty, draft, roles } = this.state;
        if (!dirty.size) return;
        this.setState({ saving: true });
        try {
            for (const role of dirty) {
                const res = await this.data.updatePermissions(role, {
                    screens: [...draft[role].screens],
                    actions: [...draft[role].actions]
                });
                if (!res?.success) throw new Error(res?.message || `No se pudo guardar ${role}`);
            }
            const labels = roles.filter((r) => dirty.has(r.role)).map((r) => r.label);
            this.props.onMessage?.('success', `Permisos guardados: ${labels.join(', ')}`);
            await this.load();
        } catch (err) {
            this.props.onMessage?.('error', err?.response?.data?.message || err?.message || 'No se pudieron guardar los permisos');
        } finally {
            this.setState({ saving: false });
        }
    }

    async reset(role) {
        this.setState({ saving: true });
        try {
            const res = await this.data.resetPermissions(role);
            if (!res?.success) throw new Error(res?.message || 'No se pudo restablecer');
            this.props.onMessage?.('success', 'Permisos restablecidos a los valores por defecto');
            await this.load();
        } catch (err) {
            this.props.onMessage?.('error', err?.response?.data?.message || err?.message || 'No se pudo restablecer');
        } finally {
            this.setState({ saving: false });
        }
    }

    /** Inconsistent combinations worth pointing out before saving. */
    get warnings() {
        const { roles = [], draft = {} } = this.state;
        const out = [];
        for (const r of roles.filter((x) => x.editable)) {
            const d = draft[r.role];
            if (!d) continue;
            if (PRICE_SCREENS.some((k) => d.screens.has(k)) && !d.actions.has('prices.view')) {
                out.push(`${r.label} puede abrir pantallas de precio pero no tiene "Ver precios".`);
            }
            if (d.actions.has('store-overrides.manage') && !d.screens.has('segments') && !d.screens.has('price')) {
                out.push(`${r.label} tiene "Excepciones por piso" pero no puede abrir Segmentaciones ni Lista de precio.`);
            }
            if (!d.screens.size) out.push(`${r.label} no tendrá ninguna pantalla.`);
        }
        return out;
    }
}

export default PermissionsController;
